import { useState, useEffect, useCallback } from 'react';
import { Plus, Loader2, FileText, X, Trash2, LayoutGrid } from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR, formatDate } from '@/utils/formatters';
import { ProductGridSelector } from '@/components/shared/ProductGridSelector';
import { joinPersonName } from '@/utils/names';

// Types
interface DebitNote {
  id: string;
  number: string;
  date?: string;
  createdAt?: string;
  invoiceRef?: string;
  invoiceNumber?: string;
  invoiceId?: string;
  invoice?: { number: string } | null;
  contactName?: string;
  contactId?: string;
  contact?: { firstName: string; lastName: string } | null;
  amount?: number;
  subtotal?: number;
  total?: number;
  status: string;
  items?: any[];
}

interface Contact { id: string; firstName: string; lastName: string; }
interface Invoice { id: string; number: string; }
interface LineItem { description: string; hsnSac: string; quantity: number; rate: number; gstRate: number; }

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  issued: 'bg-blue-50 text-blue-700',
  applied: 'bg-green-50 text-green-700',
  void: 'bg-red-50 text-red-700',
};

const GST_RATES = [0, 5, 12, 18, 28];
const emptyItem = (): LineItem => ({ description: '', hsnSac: '', quantity: 1, rate: 0, gstRate: 18 });

export function DebitNotesPage() {
  const [debitNotes, setDebitNotes] = useState<DebitNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [form, setForm] = useState({ reason: '', contactId: '', invoiceId: '', buyerGstin: '' });
  const [items, setItems] = useState<LineItem[]>([emptyItem()]);
  const [showGrid, setShowGrid] = useState(false);

  const fetchDebitNotes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/debit-notes');
      setDebitNotes(res.data.data?.debitNotes ?? res.data.data ?? []);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setDebitNotes([]);
        setError(null);
      } else {
        setError(err.response?.data?.error || 'Failed to load debit notes');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDebitNotes(); }, [fetchDebitNotes]);

  const openCreateModal = () => {
    api.get('/contacts').then(res => setContacts(res.data.data?.contacts ?? res.data.data ?? [])).catch(() => {});
    api.get('/invoices').then(res => setInvoices(res.data.data?.invoices ?? res.data.data ?? [])).catch(() => {});
    setForm({ reason: '', contactId: '', invoiceId: '', buyerGstin: '' });
    setItems([emptyItem()]);
    setShowModal(true);
  };

  const updateItem = (idx: number, field: keyof LineItem, value: string | number) => {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };
  const removeItem = (idx: number) => { if (items.length <= 1) return; setItems(prev => prev.filter((_, i) => i !== idx)); };

  const handleProductSelect = (product: any) => {
    setItems(prev => [...prev, {
      description: product.name,
      hsnSac: product.hsnCode || '',
      quantity: 1,
      rate: Number(product.unitPrice),
      gstRate: Number(product.gstRate) || 18,
    }]);
  };

  const computeTotal = () => items.reduce((sum, item) => {
    const lineAmount = item.quantity * item.rate;
    return sum + lineAmount + lineAmount * (item.gstRate / 100);
  }, 0);

  const handleCreate = async () => {
    try {
      setSaving(true);
      await api.post('/debit-notes', {
        reason: form.reason,
        contactId: form.contactId || undefined,
        invoiceId: form.invoiceId || undefined,
        buyerGstin: form.buyerGstin || undefined,
        items: items.map(i => ({ description: i.description, hsnSac: i.hsnSac || undefined, quantity: i.quantity, rate: i.rate, gstRate: i.gstRate })),
      });
      setShowModal(false);
      fetchDebitNotes();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create debit note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Debit Notes</h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Manage debit notes and adjustments</p>
        </div>
        <button onClick={openCreateModal}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors">
          <Plus size={16} /> Create Debit Note
        </button>
      </div>

      {/* Table */}
      <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)' }}>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
            <span className="ml-2 text-sm" style={{ color: 'var(--text-muted)' }}>Loading debit notes...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-red-500">
            <p className="text-sm">{error}</p>
            <button onClick={fetchDebitNotes} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
          </div>
        ) : debitNotes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20" style={{ color: 'var(--text-muted)' }}>
            <FileText size={40} className="mb-2 opacity-40" />
            <p className="text-sm">No debit notes found</p>
            <p className="text-xs">Debit notes will appear here when created</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs font-medium uppercase tracking-wider"
                  style={{ backgroundColor: 'var(--bg-inset)', borderColor: 'var(--border-primary)', color: 'var(--text-muted)' }}>
                  <th className="px-4 py-3">DN #</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Invoice Ref</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-center w-16">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: 'var(--border-primary)' }}>
                {debitNotes.map((dn) => (
                  <tr key={dn.id} className="transition-colors"
                    style={{ backgroundColor: 'var(--bg-surface)' }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--bg-inset)')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'var(--bg-surface)')}>
                    <td className="px-4 py-3 font-medium text-[var(--ink-0)]">{dn.number || '-'}</td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-muted)' }}>{dn.date || dn.createdAt ? formatDate(dn.date || dn.createdAt!) : '-'}</td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-muted)' }}>{dn.invoiceRef || dn.invoiceNumber || dn.invoice?.number || '-'}</td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-muted)' }}>
                      {dn.contactName || (dn.contact ? `${joinPersonName(dn.contact.firstName, dn.contact.lastName)}` : '-')}
                    </td>
                    <td className="px-4 py-3 text-right font-medium" style={{ color: 'var(--text-primary)' }}>{formatINR(dn.total ?? dn.subtotal ?? dn.amount ?? 0)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[dn.status] || STATUS_COLORS.draft}`}>
                        {(dn.status || 'draft').charAt(0).toUpperCase() + (dn.status || 'draft').slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => {
                          if (window.confirm(`Delete debit note ${dn.number}?`)) {
                            api.delete(`/debit-notes/${dn.id}`).then(() => fetchDebitNotes()).catch(() => alert('Failed to delete'));
                          }
                        }}
                        className="inline-flex items-center justify-center rounded-md p-1.5 transition-colors hover:bg-red-50 hover:text-red-600"
                        style={{ color: 'var(--text-muted)' }} title="Delete">
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Debit Note Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowModal(false)}>
          <div className="mx-4 w-full max-w-2xl rounded-xl shadow-xl max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: 'var(--bg-surface)' }} onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 rounded-t-xl flex items-center justify-between border-b px-6 py-4"
              style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)' }}>
              <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Create Debit Note</h2>
              <button onClick={() => setShowModal(false)} style={{ color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Reason *</label>
                <input type="text" value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="Reason for debit note"
                  className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                  style={{ backgroundColor: 'var(--bg-inset)', borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Contact</label>
                  <select value={form.contactId} onChange={e => setForm({ ...form, contactId: e.target.value })}
                    className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none"
                    style={{ backgroundColor: 'var(--bg-inset)', borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}>
                    <option value="">None</option>
                    {contacts.map(c => <option key={c.id} value={c.id}>{joinPersonName(c.firstName, c.lastName)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Invoice Reference</label>
                  <select value={form.invoiceId} onChange={e => setForm({ ...form, invoiceId: e.target.value })}
                    className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none"
                    style={{ backgroundColor: 'var(--bg-inset)', borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}>
                    <option value="">None</option>
                    {invoices.map(inv => <option key={inv.id} value={inv.id}>{inv.number}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Buyer GSTIN</label>
                <input type="text" value={form.buyerGstin} onChange={e => setForm({ ...form, buyerGstin: e.target.value })} placeholder="22AAAAA0000A1Z5"
                  className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                  style={{ backgroundColor: 'var(--bg-inset)', borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }} />
              </div>
              {/* Line Items */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Line Items *</label>
                  <div className="flex gap-2">
                    <button onClick={() => setShowGrid(true)} className="flex items-center gap-1 text-xs font-medium text-[var(--ink-0)] hover:underline"><LayoutGrid size={12} /> Browse Products</button>
                    <button onClick={() => setItems([...items, emptyItem()])} className="text-xs text-[var(--ink-0)] hover:underline">+ Add Item</button>
                  </div>
                </div>
                <div className="space-y-3">
                  {items.map((item, idx) => (
                    <div key={idx} className="rounded-lg border p-3" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-inset)' }}>
                      <div className="mb-2 flex items-start gap-2">
                        <input type="text" value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} placeholder="Description *"
                          className="flex-1 rounded-lg border px-3 py-1.5 text-sm focus:outline-none"
                          style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }} />
                        <input type="text" value={item.hsnSac} onChange={e => updateItem(idx, 'hsnSac', e.target.value)} placeholder="HSN/SAC"
                          className="w-24 rounded-lg border px-3 py-1.5 text-sm focus:outline-none"
                          style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }} />
                        {items.length > 1 && (
                          <button onClick={() => removeItem(idx)} className="rounded p-1 hover:bg-red-50 hover:text-red-600" style={{ color: 'var(--text-muted)' }}><Trash2 size={14} /></button>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="mb-0.5 block text-xs" style={{ color: 'var(--text-muted)' }}>Qty</label>
                          <input type="number" value={item.quantity} onChange={e => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                            className="w-full rounded-lg border px-2 py-1.5 text-sm focus:outline-none"
                            style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }} />
                        </div>
                        <div>
                          <label className="mb-0.5 block text-xs" style={{ color: 'var(--text-muted)' }}>Rate</label>
                          <input type="number" value={item.rate} onChange={e => updateItem(idx, 'rate', parseFloat(e.target.value) || 0)}
                            className="w-full rounded-lg border px-2 py-1.5 text-sm focus:outline-none"
                            style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }} />
                        </div>
                        <div>
                          <label className="mb-0.5 block text-xs" style={{ color: 'var(--text-muted)' }}>GST %</label>
                          <select value={item.gstRate} onChange={e => updateItem(idx, 'gstRate', parseInt(e.target.value))}
                            className="w-full rounded-lg border px-2 py-1.5 text-sm focus:outline-none"
                            style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)' }}>
                            {GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                          </select>
                        </div>
                      </div>
                      <p className="mt-1 text-right text-xs" style={{ color: 'var(--text-muted)' }}>Line total: {formatINR(item.quantity * item.rate * (1 + item.gstRate / 100))}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-right text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Grand Total: {formatINR(computeTotal())}</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t px-6 py-4" style={{ borderColor: 'var(--border-primary)' }}>
              <button onClick={() => setShowModal(false)}
                className="rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
                style={{ borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}>Cancel</button>
              <button onClick={handleCreate} disabled={saving || !form.reason || items.length === 0 || !items[0].description}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
                {saving && <Loader2 size={14} className="animate-spin" />}
                Create Debit Note
              </button>
            </div>
          </div>
        </div>
      )}

      <ProductGridSelector open={showGrid} onClose={() => setShowGrid(false)} onSelect={handleProductSelect} />
    </div>
  );
}
